import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, ChevronDown, PackagePlus } from "lucide-react";
import { isLowStock, restockMilli, type InventoryItem } from "@mango-moons/domain";
import { navigate, useCan, useData } from "../state";
import { cx, qty } from "./format";
import { Button, Modal } from "./ui";

/** Stock items at or below their reorder level, the emptiest first. */
export function useLowStock(): InventoryItem[] {
  const stock = useData("inventoryItems");
  return useMemo(() => stock.filter(isLowStock).sort((a, b) => a.stockMilli / a.reorderMilli - b.stockMilli / b.reorderMilli || a.name.localeCompare(b.name)), [stock]);
}

/** One line per item: what is left, the reorder level and how much to buy to fill it back up. */
export function LowStockList({ items, compact = false }: { items: InventoryItem[]; compact?: boolean }) {
  return <ul className={cx("space-y-2", compact && "space-y-1.5")}>{items.map((item) => {
    const out = item.stockMilli <= 0;
    return <li key={item.id} className={cx("flex items-center justify-between gap-3 rounded-xl border", compact ? "px-3 py-2 text-sm" : "px-4 py-3", out ? "border-bad/50 bg-bad/10" : "border-mango/40 bg-mango/10")}>
      <span className="min-w-0"><strong className="block truncate">{item.name}</strong>
        <small className={cx("text-xs", out ? "text-bad" : "text-mango")}>{out ? "Out of stock" : `${qty(item.stockMilli)} ${item.unit} left`} · reorder at {qty(item.reorderMilli)} {item.unit}</small></span>
      <span className="shrink-0 text-right leading-tight"><small className="block text-[10px] uppercase tracking-wider text-faint">Re-stock</small>
        <b className={cx("tabular-nums", compact ? "text-base" : "text-lg", out ? "text-bad" : "text-gold")}>{qty(restockMilli(item))} {item.unit}</b></span>
    </li>;
  })}</ul>;
}

/**
 * The header warning: a red button with the number of items running low, on every screen, and the full list as soon
 * as someone signs in. A new sign-in shows it again, so the next cashier on the till knows too.
 */
export function LowStockAlert() {
  const can = useCan();
  const low = useLowStock();
  const [open, setOpen] = useState(false);
  const [shown, setShown] = useState(false);
  const relevant = can("orders.create") || can("inventory.manage") || can("reports.read");
  useEffect(() => {
    if (shown || !relevant) return;
    if (low.length > 0) { setOpen(true); setShown(true); return; }
    // The stock list arrives a moment after sign-in; if nothing is low by then, later drops only light the button.
    const timer = setTimeout(() => setShown(true), 15_000);
    return () => clearTimeout(timer);
  }, [low.length, shown, relevant]);
  if (!relevant || low.length === 0) return null;
  const out = low.filter((item) => item.stockMilli <= 0).length;
  return <>
    <button onClick={() => setOpen(true)} title="Stock running low" className={cx("flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-bold", out > 0 ? "animate-pulse border-bad/60 bg-bad/20 text-bad" : "border-mango/50 bg-mango/15 text-mango")}>
      <AlertTriangle size={14}/>{low.length} low stock{out > 0 && ` · ${out} out`}
    </button>
    {open && <Modal open onClose={() => setOpen(false)} eyebrow="Stock alert" title={out > 0 ? `${out} item${out > 1 ? "s" : ""} out of stock, ${low.length - out} running low` : `${low.length} item${low.length > 1 ? "s" : ""} running low`} footer={<>
      {can("inventory.manage") && <Button icon={PackagePlus} className="mr-auto" onClick={() => { setOpen(false); navigate("inventory"); }}>Record purchase</Button>}
      <Button variant="primary" onClick={() => setOpen(false)}>OK</Button></>}>
      <div className="space-y-3">
        <p className="text-sm text-mute">Re-stock these before they run out.{out > 0 && " Dishes that use an item that is out cannot be billed until the purchase is recorded."}</p>
        <LowStockList items={low}/>
      </div>
    </Modal>}
  </>;
}

/** A folding strip beside the cart on the sale screen. */
export function LowStockStrip() {
  const low = useLowStock();
  const [open, setOpen] = useState(false);
  if (low.length === 0) return null;
  const out = low.filter((item) => item.stockMilli <= 0).length;
  return <div className="mx-5 mb-3 rounded-2xl border border-mango/40 bg-mango/[0.07]">
    <button onClick={() => setOpen(!open)} className={cx("flex w-full items-center gap-2 px-3.5 py-2.5 text-left text-sm font-semibold", out > 0 ? "text-bad" : "text-mango")}>
      <AlertTriangle size={16}/><span className="flex-1">{out > 0 ? `${out} out of stock · ${low.length - out} low` : `${low.length} stock item${low.length > 1 ? "s" : ""} low`}</span>
      <ChevronDown size={16} className={cx("transition-transform", open && "rotate-180")}/>
    </button>
    {open && <div className="max-h-56 overflow-y-auto px-2 pb-2"><LowStockList items={low} compact/></div>}
  </div>;
}
